// ============================================================
// exportUtils.js
// CSV and JSON export helpers
// ============================================================

/**
 * Trigger a browser file download.
 */
function downloadFile(content, filename, mimeType) {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Escape a CSV cell value.
 */
function csvCell(val) {
  if (val === null || val === undefined) return "";
  const str = String(val).replace(/"/g, '""');
  return `"${str}"`;
}

/**
 * Export full audit data to CSV.
 */
export function exportToCSV(auditInfo, categories, responses) {
  const headers = [
    "Program",
    "Citation",
    "Requirement",
    "Compliance Status",
    "Priority",
    "Auditor Notes",
    "Action Item",
    "Responsible Person",
    "Due Date",
    "Completion Status",
  ];

  const rows = [headers.map(csvCell).join(",")];

  categories.forEach((cat) => {
    cat.criteria.forEach((crit) => {
      const r = responses[crit.id] || {};
      rows.push(
        [
          cat.name,
          crit.citation,
          crit.requirement,
          r.status || "",
          r.priority || "",
          r.notes || "",
          r.actionItem || "",
          r.responsiblePerson || "",
          r.dueDate || "",
          r.completionStatus || "",
        ]
          .map(csvCell)
          .join(",")
      );
    });
  });

  const filename = `OSHA_Audit_${safeFilename(auditInfo.companyName)}_${auditInfo.auditDate}.csv`;
  downloadFile(rows.join("\n"), filename, "text/csv;charset=utf-8;");
}

/**
 * Export full audit data to JSON.
 */
export function exportToJSON(auditInfo, categories, responses) {
  const data = {
    auditInfo,
    exportedAt: new Date().toISOString(),
    categories: categories.map((cat) => ({
      id: cat.id,
      name: cat.name,
      citation: cat.citation,
      criteria: cat.criteria.map((crit) => ({
        id: crit.id,
        citation: crit.citation,
        requirement: crit.requirement,
        response: responses[crit.id] || {},
      })),
    })),
  };

  const filename = `OSHA_Audit_${safeFilename(auditInfo.companyName)}_${auditInfo.auditDate}.json`;
  downloadFile(JSON.stringify(data, null, 2), filename, "application/json");
}

/**
 * Export action items only to CSV.
 */
export function exportActionItemsCSV(auditInfo, categories, responses) {
  const headers = [
    "Program",
    "Citation",
    "Requirement",
    "Compliance Status",
    "Priority",
    "Action Item",
    "Responsible Person",
    "Due Date",
    "Completion Status",
  ];

  const rows = [headers.map(csvCell).join(",")];

  categories.forEach((cat) => {
    cat.criteria.forEach((crit) => {
      const r = responses[crit.id] || {};
      if (
        r.actionItem?.trim() ||
        r.status === "Non-Compliant" ||
        r.status === "Partially Compliant"
      ) {
        rows.push(
          [
            cat.name,
            crit.citation,
            crit.requirement,
            r.status || "",
            r.priority || "",
            r.actionItem || "",
            r.responsiblePerson || "",
            r.dueDate || "",
            r.completionStatus || "",
          ]
            .map(csvCell)
            .join(",")
        );
      }
    });
  });

  const filename = `OSHA_ActionItems_${safeFilename(auditInfo.companyName)}_${auditInfo.auditDate}.csv`;
  downloadFile(rows.join("\n"), filename, "text/csv;charset=utf-8;");
}

function safeFilename(str) {
  return (str || "Unnamed").replace(/[^a-zA-Z0-9_-]/g, "_").substring(0, 40);
}
